import { InternalRefetchQueriesInclude } from "@apollo/client";
import {
    useCreateDraftOrderMutation,
    usePublishDraftBookingMutation,
} from "generated/graphql";
import { useMemo, useRef } from "react";

type TransactArgs = {
    draftOrderId: string;
};

type UseUpdatePublishedBookingArgs = {
    /**
     * ID of the currently published booking to edit
     */
    bookingId: string;
};

type PublishBooking = (options: {
    refetchQueries?: InternalRefetchQueriesInclude;
}) => Promise<void>;

type UseUpdatePublishedBookingReturn = {
    /**
     * Take an action with the id of a draft order. Use the returned function to publish the booking.
     * `transact` gives the same id until the draft order is published
     */
    transact: (
        action: (args: TransactArgs) => Promise<void>,
    ) => Promise<PublishBooking>;
};

/**
 * Returns a function that updates a published booking
 */
export const useUpdatePublishedBooking = ({
    bookingId,
}: UseUpdatePublishedBookingArgs): UseUpdatePublishedBookingReturn => {
    const [createDraftOrder] = useCreateDraftOrderMutation();
    const [publishDraftBooking] = usePublishDraftBookingMutation();

    const draftOrderRef = useRef<ReturnType<typeof createDraftOrder> | null>(
        null,
    );

    return useMemo(
        () => ({
            transact: async (action: (args: TransactArgs) => Promise<void>) => {
                if (!draftOrderRef.current) {
                    draftOrderRef.current = createDraftOrder({
                        variables: {
                            orderId: bookingId,
                        },
                    });
                }

                const { data: createDraftData } = await draftOrderRef.current;

                if (
                    createDraftData?.createDraftOrder.__typename !==
                    "DraftOrder"
                ) {
                    throw new Error("Failed to create draft order");
                }

                await action({
                    draftOrderId: createDraftData.createDraftOrder._id,
                });

                return async ({ refetchQueries }) => {
                    if (!draftOrderRef.current) {
                        throw new Error("There is no draft order to publish");
                    }

                    const { data: draftOrderData } =
                        await draftOrderRef.current;

                    const draftOrderId =
                        draftOrderData?.createDraftOrder.__typename ===
                        "DraftOrder"
                            ? draftOrderData.createDraftOrder._id
                            : null;

                    if (!draftOrderId) {
                        throw new Error("There is no draft order to publish");
                    }

                    const { data } = await publishDraftBooking({
                        variables: {
                            publishDraftBookingId: draftOrderId,
                        },
                        refetchQueries, //This will update action item counts and the like.
                        awaitRefetchQueries: true,
                    });

                    if (data?.publishDraftBooking.__typename !== "Booking") {
                        throw new Error("Failed to publish booking");
                    }

                    draftOrderRef.current = null;
                };
            },
        }),
        [bookingId, createDraftOrder, publishDraftBooking],
    );
};
